import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type Group, isApiError, useUpdateGroup } from "@/lib/api";
import { logger } from "@/lib/logger";

interface RenameGroupDialogProps {
  group: Group | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RenameGroupDialog({ group, open, onOpenChange }: RenameGroupDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rename Group</DialogTitle>
          <DialogDescription className="sr-only">Rename {group?.name}</DialogDescription>
        </DialogHeader>
        {group && open && (
          // Remount with fresh initial state whenever the target group
          // changes, so the field reflects the new group without a sync effect.
          <RenameGroupForm key={group.id} group={group} onCancel={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface RenameGroupFormProps {
  group: Group;
  onCancel: () => void;
}

function RenameGroupForm({ group, onCancel }: RenameGroupFormProps) {
  const updateGroup = useUpdateGroup();
  const [name, setName] = useState(group.name);
  const [error, setError] = useState("");
  const saving = updateGroup.isPending;

  const handleSave = async () => {
    setError("");

    const trimmed = name.trim();
    if (!trimmed) {
      setError("Please enter a group name.");
      return;
    }

    try {
      await updateGroup.mutateAsync({ groupId: group.id, name: trimmed });
      onCancel();
    } catch (err) {
      logger.error("[rename-group] failed:", err);
      const detail = isApiError(err)
        ? (err.body as { detail?: string } | undefined)?.detail
        : undefined;
      setError(detail ?? "Failed to rename group. Please try again.");
    }
  };

  return (
    <DialogBody>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="rename-group-name">Group name</Label>
          <Input
            id="rename-group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Finance Team"
            maxLength={100}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSave();
              }
            }}
          />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>

      <DialogFooter className="mt-4">
        <Button variant="outline" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </DialogFooter>
    </DialogBody>
  );
}
